import { FC, Fragment, useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Formik, useFormik } from "formik";
import { t } from "i18next";

import {
  useStripe,
  useElements,
  Elements,
  CardElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { PlanListItem } from "@/shared/components/molecules/SubscriptionListItem";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { PaymentMethodCard } from "@/shared/components/molecules/PaymentMethodCard";
import { PlanList } from "@/shared/components/molecules/SubscriptionList";
import { SubscriptionCard } from "@/shared/components/molecules/SubscriptionCard";
import { SelectCountry } from "@/shared/components/organisms/SelectCountry";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { Select } from "@/shared/components/organisms/Select";
import { Modal } from "@/shared/components/organisms/Modal";
import { Button } from "@/shared/components/atoms/Button";
import { useDebounce } from "@/shared/hooks/debounce";
import { toasts } from "@/shared/utils/toasts";

import { useGetAllStatesQuery } from "@/app/app.apiStates";
import { useCountriesQuery } from "@/app/app.apiCountry";

import { Page } from "@/shared/components/atoms/Page";
import { PaymentIntentCard } from "@/shared/components/molecules/PaymentIntentCard";
import { Empty } from "@/shared/components/organisms/Empty";
import {
  useUpdateSubscriptionMutation,
  useCreateCustomerMutation,
  useCreateSetupIntentMutation,
  useCreateSubscriptionMutation,
  useGetCustomerQuery,
  useGetInvoicesQuery,
  useGetPaymentIntentsQuery,
  useGetPaymentMethodsQuery,
  useGetPlansQuery,
  useUpdateCustomerMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useGetPromotionCodeQuery,
  useGetSubscriptionQuery,
} from "@/app/billing/billing.api";
import {
  customerInitialValues,
  customerValidationSchema,
} from "@/app/billing/customer.schema";
import { useGetBranchQuery } from "@/app/branchs/branchs.api";
import { Plan } from "@/app/billing/plan";
import { CreateSubscriptionForm } from "@/shared/components/organisms/CreateSubscriptionForm";
import {
  createSubscriptionInitialValues,
  CreateSubscriptionValues,
} from "@/app/billing/subscription.schema";
import { InvoiceCard } from "@/shared/components/molecules/InvoiceCard";
import { Invoice } from "@/app/billing/invoice";
import { PaymentCardInput } from "@/shared/components/molecules/PaymentCardInput";
import { PaymentMethod } from "@/app/billing/payment-method";
import { useDispatch } from "react-redux";

export type SubscriptionsPageProps = {};

export const SubscriptionsPageContent: FC<SubscriptionsPageProps> = () => {
  const { id } = useParams();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [cancelSubscriptionVisible, setCancelSubscriptionVisible] =
    useState(false);
  const [contractVisible, setContractVisible] = useState(false);
  const [customerFormVisible, setCustomerFormVisible] = useState(false);
  const [invoicesVisible, setInvoicesVisible] = useState(false);
  const [paymentIntentsVisible, setPaymentIntentsVisible] = useState(false);
  const [paymentMethodsVisible, setPaymentMethodsVisible] = useState(false);
  const [createSubscriptionFormVisible, setCreateSubscriptionFormVisible] =
    useState(false);

  const [plan, setPlan] = useState<any>();

  const [createSubscription, createSubscriptionResult] =
    useCreateSubscriptionMutation();
  const [updateSubscription, updateSubscriptionResult] =
    useUpdateSubscriptionMutation();
  const [createSetupIntent, createSetupIntentResult] =
    useCreateSetupIntentMutation();
  const [updatePaymentMethod, updatePaymentMethodResult] =
    useUpdatePaymentMethodMutation();
  const [deletePaymentMethod, deletePaymentMethodResult] =
    useDeletePaymentMethodMutation();

  const [createCustomer, createCustomerResult] = useCreateCustomerMutation();
  const [updateCustomer, updateCustomerResult] = useUpdateCustomerMutation();
  const getStatesQuery = useGetAllStatesQuery({});

  const getBranchQuery = useGetBranchQuery({
    branchId: id as string,
  });

  const stripeCustomerId = useMemo(() => {
    return getBranchQuery.data?.stripe_customer_id;
  }, [getBranchQuery.data]);

  const getSubscriptionQuery = useGetSubscriptionQuery({
    branchId: id as string,
  });

  const searchFormik = useFormik({
    initialValues: { search: "", page: 1 },
    onSubmit: () => {},
  });

  const debouncedSearch = useDebounce(searchFormik.values, 500);

  const getPlansQuery = useGetPlansQuery({
    branchId: id,
    search: debouncedSearch.search,
  });

  const getInvoicesQuery = useGetInvoicesQuery(
    {
      branchId: id,
      customerId: stripeCustomerId,
    },
    { skip: !stripeCustomerId }
  );

  const getCustomerQuery = useGetCustomerQuery(
    {
      branchId: id,
      id: stripeCustomerId,
    },
    { skip: !stripeCustomerId }
  );

  const getPaymentMethodsQuery = useGetPaymentMethodsQuery(
    {
      branchId: id,
      customerId: stripeCustomerId,
    },
    {
      skip: !stripeCustomerId,
    }
  );

  const getPaymentIntentsQuery = useGetPaymentIntentsQuery(
    {
      branchId: id,
      customerId: stripeCustomerId,
    },
    {
      skip: !stripeCustomerId,
    }
  );

  const customerFormik = useFormik({
    initialValues: customerInitialValues,
    validationSchema: customerValidationSchema,
    onSubmit: (values) => {
      if (stripeCustomerId) {
        updateCustomer({
          branchId: id,
          ...values,
        });
      } else {
        createCustomer({
          branchId: id,
          ...values,
        });
      }
    },
  });

  useEffect(() => {
    if (getCustomerQuery.isSuccess) {
      customerFormik.setValues(getCustomerQuery.data);
    }

    if (getCustomerQuery.isError) {
      toasts.danger(
        t("contexts.billing.get_customer_failure"),
        t("contexts.billing.get_customer_failure_description")
      );
    }
  }, [getCustomerQuery]);

  useEffect(() => {
    (async () => {
      if (createSubscriptionResult.isSuccess) {
        console.debug(createSubscriptionResult.data);
        if (createSubscriptionResult.data.clientSecret) {
          const confirmation = await stripe.confirmCardPayment(
            createSubscriptionResult.data?.clientSecret,
            {
              payment_method: {
                card: elements.getElement(CardElement),
                billing_details: {
                  name: getCustomerQuery.data?.name,
                  email: getCustomerQuery.data?.email,
                  phone: getCustomerQuery.data?.phone,
                  address: {
                    city: getCustomerQuery.data?.address?.city,
                    country: getCustomerQuery.data?.address?.country,
                    line1: getCustomerQuery.data?.address?.line1,
                    line2: getCustomerQuery.data?.address?.line2,
                    postal_code: getCustomerQuery.data?.address?.postal_code,
                  },
                },
              },
            }
          );
          console.debug({ confirmation });
        }

        toasts.success(
          t("contexts.billing.create_subscription_success"),
          t("contexts.billing.create_subscription_success_description")
        );

        createSubscriptionResult.reset();
        getPaymentMethodsQuery.refetch();
        getPaymentIntentsQuery.refetch();
        subscriptionFormik.resetForm();
        setCreateSubscriptionFormVisible(false);
      }

      if (createSubscriptionResult.isError) {
        toasts.danger(
          t("contexts.billing.create_subscription_failure"),
          t("contexts.billing.create_subscription_failure_description")
        );
      }
    })();
  }, [createSubscriptionResult]);

  useEffect(() => {
    if (updateSubscriptionResult.isSuccess) {
      toasts.success(
        t("contexts.billing.update_subscription_success"),
        t("contexts.billing.update_subscription_success_description")
      );
      updateSubscriptionResult.reset();
      setCancelSubscriptionVisible(false);
    }

    if (updateSubscriptionResult.isError) {
      toasts.danger(
        t("contexts.billing.update_subscription_failure"),
        t("contexts.billing.update_subscription_failure_description")
      );
      updateSubscriptionResult.reset();
      setCancelSubscriptionVisible(false);
    }
  }, [updateSubscriptionResult]);

  const subscriptionFormik = useFormik({
    initialValues: createSubscriptionInitialValues,
    onSubmit: async (values: CreateSubscriptionValues) => {
      try {
        if (!getCustomerQuery.data?.id) {
          toasts.danger(
            t("contexts.billing.create_subscription_failure"),
            t("contexts.billing.create_subscription_failure_description")
          );

          return;
        }

        createSubscription({
          customerId: stripeCustomerId,
          branchId: id,
          paymentMethodId: values.paymentMethodId,
          planId: values.planId,
          priceId: values.priceId,
          code: values.code,
          addOns: values.addOns,
        });
      } catch (error) {
        toasts.danger(
          t("contexts.billing.create_payment_method_failure"),
          t("contexts.billing.create_payment_method_failure_description")
        );
      }
    },
  });

  const getPromotionCodeQuery = useGetPromotionCodeQuery(
    {
      code: subscriptionFormik.values.code,
      branchId: id,
    },
    {
      skip: subscriptionFormik.values.code.length < 3,
    }
  );

  useEffect(() => {
    if (subscriptionFormik.values.code == "") {
      console.debug("invalidating tags");
    }
  }, [subscriptionFormik.values.code]);

  console.debug(getPromotionCodeQuery.data);

  const countriesQuery = useCountriesQuery(undefined);

  const handleCreatePaymentMethod = useCallback(async () => {
    try {
      if (!getCustomerQuery.data?.id) {
        toasts.danger(
          t("contexts.billing.create_payment_method_failure"),
          t("contexts.billing.create_payment_method_failure_description")
        );

        return;
      }

      const result = await createSetupIntent({
        customerId: stripeCustomerId,
        branchId: id,
      }).unwrap();

      await stripe.confirmCardSetup(result.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: getCustomerQuery.data?.name,
            email: getCustomerQuery.data?.email,
            phone: getCustomerQuery.data?.phone,
            address: {
              city: getCustomerQuery.data?.address?.city,
              country: getCustomerQuery.data?.address?.country,
              line1: getCustomerQuery.data?.address?.line1,
              line2: getCustomerQuery.data?.address?.line2,
              postal_code: getCustomerQuery.data?.address?.postal_code,
            },
          },
        },
      });

      elements.getElement(CardElement).clear();

      toasts.success(
        t("contexts.billing.create_payment_method_success"),
        t("contexts.billing.create_payment_method_success_description")
      );

      getPaymentMethodsQuery.refetch();
    } catch (error) {
      toasts.danger(
        t("contexts.billing.create_payment_method_failure"),
        t("contexts.billing.create_payment_method_failure_description")
      );
    }
  }, [stripeCustomerId, id, stripe, getCustomerQuery]);

  const handleDeletePaymentMethod = useCallback(
    (paymentMethodId: string) =>
      deletePaymentMethod({
        branchId: id,
        id: paymentMethodId,
      }),
    [id]
  );

  const handleUpdatePaymentMethod = useCallback(
    (paymentMethodId: string) =>
      updatePaymentMethod({
        branchId: id,
        id: paymentMethodId,
        default: true,
      }),
    [id]
  );

  const handleCancelSubscription = useCallback(() => {
    setCancelSubscriptionVisible(true);
  }, []);

  const handleCancelSubscriptionConfirm = useCallback(() => {
    updateSubscription({
      status: "cancelled",
      branchId: id,
    });
  }, [id]);

  const handleCancelSubscriptionCancel = useCallback(() => {
    setCancelSubscriptionVisible(false);
  }, []);

  const countryOptions = useMemo(() => {
    return countriesQuery.data
      ? countriesQuery.data.map((country) => ({
          ...country,
          code: country.isoCode,
          name: country.name,
        }))
      : [];
  }, [countriesQuery]);

  const states = useMemo(() => {
    const country = customerFormik.values.address.country;

    return (
      getStatesQuery.data
        ?.filter((state) => state.countryCode === country)
        ?.map((state) => ({
          id: state.isoCode,
          name: state.name,
        })) ?? []
    );
  }, [customerFormik.values.address.country, getStatesQuery.data]);

  useEffect(() => {
    if (createCustomerResult.isSuccess) {
      toasts.success(
        t("contexts.billing.create_customer_success"),
        t("contexts.billing.create_customer_success_description")
      );
    }
    if (createCustomerResult.isError) {
      toasts.success(
        t("contexts.billing.create_customer_failure"),
        t("contexts.billing.create_customer_failure_descriptoin")
      );
    }
    setCustomerFormVisible(false);
  }, [createCustomerResult]);

  useEffect(() => {
    if (updateCustomerResult.isSuccess) {
      toasts.success(
        t("contexts.billing.update_customer_success"),
        t("contexts.billing.update_customer_success_description")
      );
    }
    if (updateCustomerResult.isError) {
      toasts.danger(
        t("contexts.billing.update_customer_failure"),
        t("contexts.billing.update_customer_failure_descriptoin")
      );
    }
    setCustomerFormVisible(false);
  }, [updateCustomerResult]);

  const handlePlanPurchase = useCallback((plan: Plan) => {
    setCreateSubscriptionFormVisible(true);
    setPlan(plan);

    subscriptionFormik.setFieldValue("planId", plan.id);
    subscriptionFormik.setFieldValue("priceId", plan.price.id);
  }, []);

  return (
    <Fragment>
      <Page
        title={t("subscription.views.subscription")}
        description="Suscribete o administra tu información de pago y suscripción actual"
      >
        <PlanList
          onContractClick={() => setContractVisible(true)}
          onInvoicesClick={() => setInvoicesVisible(true)}
          onPaymentMethodsClick={() => setPaymentMethodsVisible(true)}
          onPaymentIntentsClick={() => setPaymentIntentsVisible(true)}
          onBillingInformationClick={() => setCustomerFormVisible(true)}
          isLoading={getPlansQuery.isLoading}
          onSearchChange={(search) =>
            searchFormik.setFieldValue("search", search)
          }
          search={searchFormik.values.search}
        >
          {getSubscriptionQuery?.data?.id && (
            <SubscriptionCard
              onCancel={handleCancelSubscription}
              subscription={getSubscriptionQuery.data}
            />
          )}
          {getPlansQuery.isLoading && (
            <Fragment>
              <PlanListItem isLoading />
              <PlanListItem isLoading />
              <PlanListItem isLoading />
            </Fragment>
          )}
          {getPlansQuery?.data?.data?.map((plan) => (
            <PlanListItem plan={plan} onPurchase={handlePlanPurchase} />
          ))}
        </PlanList>
      </Page>
      <DialogCreate
        OnclickCancel={() => setInvoicesVisible(false)}
        open={invoicesVisible}
        setOpen={() => setInvoicesVisible(!paymentMethodsVisible)}
        tittle={t("contexts.billing.invoices")}
        subTittle={t("contexts.billing.invoices_description")}
        tittleButtonCreate={t("contexts.shared.create")}
        tittleButtonCancel={t("contexts.shared.cancel")}
        OnclickCreate={() => {}}
        hideFooter
      >
        <form action="">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("contexts.billing.invoices")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    {getInvoicesQuery.isLoading ||
                      (getInvoicesQuery.isFetching && (
                        <ul className="flex flex-col gap-4">
                          <InvoiceCard isLoading={true} />
                          <InvoiceCard isLoading={true} />
                          <InvoiceCard isLoading={true} />
                        </ul>
                      ))}
                    {getInvoicesQuery.data?.length === 0 ? (
                      <div className="flex flex-col">
                        <Empty texto={t("contexts.billing.no_invoices")} />
                      </div>
                    ) : (
                      <ul className="flex flex-col gap-4">
                        {getInvoicesQuery.data?.map((invoice: Invoice) => {
                          return (
                            <InvoiceCard
                              onDownload={() => window.open(invoice.pdfUrl)}
                              key={invoice.id}
                              invoice={invoice}
                            />
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogCreate>
      <DialogCreate
        OnclickCancel={() => setPaymentMethodsVisible(false)}
        open={paymentMethodsVisible}
        setOpen={() => setPaymentMethodsVisible(!paymentMethodsVisible)}
        tittle={t("contexts.billing.payment_methods")}
        subTittle={t("contexts.billing.payment_methods_description")}
        tittleButtonCreate={t("contexts.shared.create")}
        tittleButtonCancel={t("contexts.shared.cancel")}
        OnclickCreate={handleCreatePaymentMethod}
      >
        <form action="">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("contexts.billing.payment_methods")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    {getPaymentMethodsQuery.isLoading ||
                    getPaymentMethodsQuery.isFetching ? (
                      <ul className="flex flex-col gap-4">
                        <PaymentMethodCard isLoading={true} />
                        <PaymentMethodCard isLoading={true} />
                        <PaymentMethodCard isLoading={true} />
                      </ul>
                    ) : (
                      <Fragment>
                        {getPaymentMethodsQuery.data?.length === 0 && (
                          <div className="flex flex-col">
                            <Empty
                              texto={t("contexts.billing.no_payment_methods")}
                            />
                          </div>
                        )}
                        <ul className="flex flex-col gap-4">
                          {getPaymentMethodsQuery.data?.map(
                            (paymentMethod: PaymentMethod) => {
                              return (
                                <PaymentMethodCard
                                  onDelete={handleDeletePaymentMethod}
                                  onSet={handleUpdatePaymentMethod}
                                  key={paymentMethod.id}
                                  paymentMethod={paymentMethod}
                                />
                              );
                            }
                          )}
                        </ul>
                      </Fragment>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("contexts.billing.create_payment_method")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <PaymentCardInput />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogCreate>
      <DialogCreate
        OnclickCreate={() => setPaymentIntentsVisible(!paymentIntentsVisible)}
        OnclickCancel={() => setPaymentIntentsVisible(false)}
        tittleButtonCreate={t("general.accept")}
        tittleButtonCancel={t("general.cancel")}
        open={paymentIntentsVisible}
        setOpen={() => setPaymentIntentsVisible(!paymentIntentsVisible)}
        tittle={t("contexts.billing.payment_intents")}
        subTittle={t("contexts.billing.payment_intents_description")}
        hideFooter
      >
        <form action="">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("contexts.billing.payment_intents")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <ul className="flex flex-col gap-4 p-4">
                      {getPaymentIntentsQuery.data?.map(
                        (paymentIntent: any) => {
                          return (
                            <PaymentIntentCard
                              key={paymentIntent.id}
                              paymentIntent={paymentIntent}
                            />
                          );
                        }
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogCreate>
      <DialogCreate
        OnclickCreate={subscriptionFormik.handleSubmit}
        OnclickCancel={() => {
          setCreateSubscriptionFormVisible(false);
        }}
        isLoading={createSubscriptionResult.isLoading}
        tittleButtonCreate={t("general.create")}
        tittleButtonCancel={t("general.cancel")}
        open={createSubscriptionFormVisible}
        setOpen={() => setCreateSubscriptionFormVisible(false)}
        tittle={t("contexts.billing.billing_information")}
        subTittle={t("contexts.billing.billing_information_description")}
      >
        <CreateSubscriptionForm
          formik={subscriptionFormik}
          plan={plan}
          isLoading={createSubscriptionResult.isLoading}
          paymentMethods={getPaymentMethodsQuery.data ?? []}
          promotionCode={getPromotionCodeQuery.data}
          isLoadingPromotionCode={getPromotionCodeQuery.isLoading}
        />
      </DialogCreate>

      <DialogCreate
        OnclickCreate={customerFormik.handleSubmit}
        OnclickCancel={() => setCustomerFormVisible(false)}
        isLoading={
          createCustomerResult.isLoading || updateCustomerResult.isLoading
        }
        tittleButtonCreate={
          stripeCustomerId
            ? t("contexts.shared.update")
            : t("contexts.shared.create")
        }
        tittleButtonCancel={t("general.cancel")}
        open={customerFormVisible}
        setOpen={() => setCustomerFormVisible(false)}
        tittle={t("contexts.billing.billing_information")}
        subTittle={t("contexts.billing.billing_information_description")}
      >
        <div className="mt-6 mx-2">
          <div className="space-y-12">
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    {t("contexts.billing.billing_information")}
                  </h2>
                </div>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_name")}
                    span={t("general.mandatory")}
                    name="name"
                    type="text"
                    placeholder={t("subscription.views.billing_name")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.name}
                    textError={customerFormik.errors.name}
                    value={customerFormik.values.name}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_id")}
                    span={t("general.mandatory")}
                    name="dni"
                    type="numbr"
                    placeholder={t("subscription.views.billing_id")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.dni}
                    textError={customerFormik.errors.dni}
                    value={customerFormik.values.dni}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_phone")}
                    span={t("general.mandatory")}
                    name="phone"
                    type="number"
                    placeholder={t("subscription.views.billing_phone")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.phone}
                    textError={customerFormik.errors.phone}
                    value={customerFormik.values.phone}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_email")}
                    span={t("general.mandatory")}
                    name="email"
                    type="email"
                    placeholder={t("subscription.views.billing_email")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.email}
                    textError={customerFormik.errors.email}
                    value={customerFormik.values.email}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_address_line1")}
                    span={t("general.mandatory")}
                    name="address.line1"
                    type="text"
                    placeholder={t("subscription.views.billing_address_line1")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.address?.line1}
                    textError={customerFormik.errors.address?.line1}
                    value={customerFormik.values.address?.line1}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("subscription.views.billing_address_line2")}
                    span={t("general.optional")}
                    name="address.line2"
                    type="text"
                    placeholder={t("subscription.views.billing_address_line2")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.address?.line2}
                    textError={customerFormik.errors.address?.line2}
                    value={customerFormik.values.address?.line2}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("branch.views.code_zip")}
                    span={t("general.mandatory")}
                    name="postalCode"
                    type="text"
                    placeholder={t("branch.views.code_zip")}
                    value={customerFormik.values.address?.postalCode}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.address?.postalCode}
                    textError={customerFormik.errors.address?.postalCode}
                  />
                </div>
                <div className="col-span-full">
                  <SelectCountry
                    selected={countryOptions?.find(
                      (country) =>
                        country.isoCode == customerFormik.values.address.country
                    )}
                    setSelected={(selected) => {
                      customerFormik.setFieldValue("country", selected.code);
                    }}
                    options={countryOptions}
                    tittle={t("branch.views.country")}
                  />
                </div>
                <div className="col-span-full">
                  <Select
                    name="state"
                    span={t("general.optional")}
                    selected={states.find(
                      (option) =>
                        option.id === customerFormik.values.address.state
                    )}
                    setSelected={(value) =>
                      customerFormik.setFieldValue("address.state", value.id)
                    }
                    options={states}
                    tittle={t("user.views.state")}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("branch.views.population")}
                    span={t("general.mandatory")}
                    name="city"
                    type="text"
                    placeholder={t("branch.views.population")}
                    onChange={customerFormik.handleChange}
                    error={!!customerFormik.errors.address?.city}
                    value={customerFormik.values.address.city}
                    textError={customerFormik.errors.address?.city}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogCreate>
      <Modal
        open={cancelSubscriptionVisible}
        Close={handleCancelSubscriptionCancel}
        tittleModal={t("contexts.billing.cancel_subscription")}
        textButtonClose={t("general.cancel")}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              text={t("contexts.shared.continue")}
              className="bg-red-500"
              onClick={handleCancelSubscriptionConfirm}
              isLoading={updateSubscriptionResult.isLoading}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <span>
                    {t("contexts.billing.cancel_subscription_description")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={contractVisible}
        Close={() => setContractVisible(false)}
        tittleModal={t("contexts.billing.billing_information")}
        textButtonClose={t("general.cancel")}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              text={t("general.accept")}
              className="bg-primary-500"
              onClick={() => {
                setContractVisible(false);
                setCustomerFormVisible(true);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <span>
                    {t("subscription.views.complete_billing_information")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

export const SubscriptionsPage: FC<SubscriptionsPageProps> = (props) => {
  return (
    <Elements
      stripe={loadStripe(
        "pk_test_51IFiBGG0cWNM28zsOlvKkO4nVmBBFGRqM32hcGPbY8eWAU74lQoC88GJKfFkNL5GzD0yFUZyPNcBExUbstqhENug00vlR6yny4"
      )}
    >
      <SubscriptionsPageContent {...props} />
    </Elements>
  );
};
